import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { useFlows } from '../hooks/useFlows';

import { pipeDeletion, systemDeletion } from '../events/pipeDeletion.event';

import { findSubscription } from '../utils/subscription.utils';
import { isProvisioning, hasProvisioningFailed } from '../redux-store/thunks/subscriptions.thunks';

import type { RootState } from '../types/state.types';
import type { StatusBarTask } from '../types/common.types';

type TaskId =
  | 'syncing-pipe'
  | 'syncing-pipes'
  | 'syncing-datasets'
  | 'syncing-systems'
  | 'deriving-flows'
  | 'provisioning'
  | 'provisioning-failed'
  | 'provisioning-failed'
  | 'deleting-pipes'
  | 'deleting-systems'
  | 'pipes-deletion-failed'
  | 'pipes-partially-deleted';

const StatusBarTasks: { [key in TaskId]: StatusBarTask } = {
  'syncing-pipe': {
    id: 'syncing-pipe',
    value: false,
    caption: 'Syncing pipe',
    tooltip: 'Syncing pipe',
    testId: 'status-bar-syncing-pipe',
  },
  'syncing-pipes': {
    id: 'syncing-pipes',
    value: false,
    caption: 'Syncing pipes',
    tooltip: 'Syncing pipes',
    testId: 'status-bar-syncing-pipes',
  },
  'syncing-datasets': {
    id: 'syncing-datasets',
    value: false,
    caption: 'Syncing datasets',
    tooltip: 'Syncing datasets',
    testId: 'status-bar-syncing-datasets',
  },
  'syncing-systems': {
    id: 'syncing-systems',
    value: false,
    caption: 'Syncing systems',
    tooltip: 'Syncing systems',
    testId: 'status-bar-syncing-systems',
  },
  'deriving-flows': {
    id: 'deriving-flows',
    value: false,
    caption: 'Deriving flows',
    tooltip: 'Deriving flows',
    testId: 'status-bar-deriving-flows',
  },
  provisioning: {
    id: 'provisioning',
    value: false,
    caption: 'Provisioning',
    tooltip: 'Subscription provisioning...',
    testId: 'status-bar-provisioning',
  },
  'provisioning-failed': {
    id: 'provisioning-failed',
    value: false,
    caption: 'provisioning-failed',
    tooltip: 'Subscription provisioning failed',
    testId: 'status-bar-provisioning-failed',
  },
  'deleting-pipes': {
    id: 'deleting-pipes',
    value: false,
    caption: 'Deleting pipes',
    tooltip: 'Deleting pipes',
    testId: 'status-bar-deleting-pipes',
  },
  'deleting-systems': {
    id: 'deleting-systems',
    value: false,
    caption: 'Deleting systems',
    tooltip: 'Deleting systems',
    testId: 'status-bar-deleting-systems',
  },
  'pipes-partially-deleted': {
    id: 'pipes-partially-deleted',
    value: false,
    caption: 'Pipes deleted partially',
    tooltip: 'Pipes deleted partially',
    testId: 'status-bar-pipes-partially-deleted',
  },
  'pipes-deletion-failed': {
    id: 'pipes-deletion-failed',
    value: false,
    caption: 'Pipes deletion failed',
    tooltip: 'Pipes deletion failed',
    testId: 'status-bar-pipes-deletion-failed',
  },
};

const updateTask = (id: TaskId, value: boolean) => ({ ...StatusBarTasks[id], value });

export const useStatusBar = () => {
  const { status: flowsStatus } = useFlows();

  const subscriptionId = useSelector((state: RootState) => state.subscription.id);
  const subscription = useSelector((state: RootState) =>
    findSubscription(state.subscriptions)(subscriptionId)
  );
  const pipeLoading = useSelector((state: RootState) => state.loadStatus.pipeLoading);
  const pipesLoading = useSelector((state: RootState) => state.loadStatus.pipesLoading);
  const datasetsLoading = useSelector((state: RootState) => state.loadStatus.datasetsLoading);
  const systemsLoading = useSelector((state: RootState) => state.loadStatus.systemsLoading);

  const tasks: StatusBarTask[] = useMemo(() => {
    const pipeLoadingStatus = updateTask('syncing-pipe', pipeLoading);
    const pipesLoadingStatus = updateTask('syncing-pipes', pipesLoading);
    const datasetsLoadingStatus = updateTask('syncing-datasets', datasetsLoading);
    const systemsLoadingStatus = updateTask('syncing-systems', systemsLoading);
    const flowsLoadingStatus = updateTask('deriving-flows', flowsStatus === 'calculating-flows');
    const provisioningStatus = updateTask('provisioning', isProvisioning(subscription));
    const provisioningFailed = updateTask(
      'provisioning-failed',
      hasProvisioningFailed(subscription)
    );

    return [
      pipeLoadingStatus,
      pipesLoadingStatus,
      datasetsLoadingStatus,
      systemsLoadingStatus,
      flowsLoadingStatus,
      provisioningStatus,
      provisioningFailed,
    ];
  }, [subscriptionId, pipeLoading, pipesLoading, datasetsLoading, systemsLoading, flowsStatus]);

  return tasks;
};

export const useStatusBarWithPipeDeletion = (tasks: StatusBarTask[]) => {
  const [currentTasks, setCurrentTasks] = useState<StatusBarTask[]>([]);

  useEffect(() => {
    setCurrentTasks(tasks);
  }, [tasks]);

  useEffect(() => {
    const remove = pipeDeletion.on(({ status }) => {
      if (status === 'start-api-delete') {
        setCurrentTasks((prev) => [...prev, updateTask('deleting-pipes', true)]);
      }

      if (
        status === 'finish-post-delete-actions' ||
        status === 'finish-api-with-error' ||
        status === 'deletion-failed' ||
        status === 'partially-deleted'
      ) {
        setCurrentTasks((prev) => {
          return prev.map((t) => {
            if (t.id === 'deleting-pipes') {
              return { ...t, value: false };
            }

            return t;
          });
        });
      }
    });

    return () => remove();
  }, []);

  return currentTasks;
};

export const useStatusBarWithSystemDeletion = (tasks: StatusBarTask[]) => {
  const [currentTasks, setCurrentTasks] = useState<StatusBarTask[]>([]);

  useEffect(() => {
    setCurrentTasks(tasks);
  }, [tasks]);

  useEffect(() => {
    const remove = systemDeletion.on(({ status }) => {
      if (status === 'start-api-delete') {
        setCurrentTasks((prev) => [...prev, updateTask('deleting-systems', true)]);
      }

      if (
        [
          'finish-post-delete-actions',
          'finish-api-with-error',
          'deletion-failed',
          'partially-deleted',
        ].includes(status)
      ) {
        setCurrentTasks((prev) => {
          return prev.map((t) => {
            if (t.id === 'deleting-systems') {
              return { ...t, value: false };
            }

            return t;
          });
        });
      }
    });

    return () => remove();
  }, []);

  return currentTasks;
};
